import { FileStatus, FilterRange, SortDirection } from './types'
import { ParamProjectTypes, ApiProjectTypes, ApiSeriesTypes, StatusTypes, MediaContentDTO, GalleryImage, ProjectDTO, BrandDTO, ProductDTO } from 'models'
import moment from 'moment'
import { CSSProperties } from 'react'
import styleAssets from 'assets/styles/_.scss'
import { FilterPeriods } from 'store'
import { OnSelectCallbackParam } from 'react-daterange-picker'

export * from './User'
export * from './validations'
export * from './Api'
export * from './Cloud'
export * from './Sprite'
export * from './types'
export * from './custom-hooks'

export const getMainImageSrcForObj = (object: any) => {
  if (!object) return null
  if (object.mainImage) return object.mainImage.url
  if (object.image) return object.image.url
  if (object.images && object.images.length) return object.images[0].url
}

export const getObjectsListSortedByNameAndDate = (list, arrangeState) => {
  const { arrangeByName, arrangeByAddedDate, arrangeOrder } = arrangeState

  return [...list].sort((a, b) => {
    const { name: nameA, createdDate: dateA } = a
    const { name: nameB, createdDate: dateB } = b

    const results = {
      name: 0,
      date: 0
    }

    if (nameA.toLowerCase() < nameB.toLowerCase()) results.name = -1
    if (nameA.toLowerCase() > nameB.toLowerCase()) results.name = 1
    if (arrangeByName === 'descending') results.name = -results.name

    if (new Date(dateA) < new Date(dateB)) results.date = -1
    if (new Date(dateA) > new Date(dateB)) results.date = 1
    if (arrangeByAddedDate === 'descending') results.date = -results.date

    return results[arrangeOrder[0]] || results[arrangeOrder[1]]
  })
}

export const getToFixed2Number = (num: number): number => {
  if (!num) return num
  return Number(num.toFixed(2))
}

export const formatNumberShort = (
  value: number | string,
  digits: number = 2
): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '0'
  }

  const num = Number(value)
  const absolute = Math.abs(num)
  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' }
  ]

  const format = (v: number, suffix = '') => {
    const fixed = v.toFixed(digits)
    const trimmed = fixed.replace(/\.?0+$/, '') // keep up to `digits` decimals
    return `${trimmed}${suffix}`
  }

  for (const unit of units) {
    if (absolute >= unit.limit) {
      return format(num / unit.limit, unit.suffix)
    }
  }

  return format(num)
}

export const getObjId = obj => {
  return obj.externalId
}

export const getObjExternalId = obj => {
  return Number(obj.externalId)
}

export const getProductBrandId = product => {
  return product.brandId || product.brandExternalId
}

const addLeadingZero = num => {
  return num < 10
    ? `0${num}`
    : `${num}`
}

export const parseToTimeCode = frameNumber => {
  const frames = frameNumber % 10
  const absSeconds = Math.floor(frameNumber / 10)
  const seconds = addLeadingZero(absSeconds % 60)
  const absMinutes = Math.floor(absSeconds / 60)
  const minutes = addLeadingZero(absMinutes % 60)
  const hours = addLeadingZero(Math.floor(absMinutes / 60))

  return `${hours}:${minutes}:${seconds},${frames}`
}

export const parseMatchParamToProjectType = projectType => {
  switch (projectType) {
    case ParamProjectTypes.MOVIES: return ApiProjectTypes.MOVIE
    case ParamProjectTypes.TV_SERIES: return ApiProjectTypes.TV_SERIES
    case ParamProjectTypes.VOV: return ApiProjectTypes.VOV

    default: return projectType
  }
}

export const getTypeAndSubtypesArr = apiProjectType => {
  switch (apiProjectType) {
    case ApiProjectTypes.TV_SERIES:
      return [ApiProjectTypes.TV_SERIES, ...Object.values(ApiSeriesTypes)]

    default: return [apiProjectType]
  }
}

export const parseAndGetTypeAndSubtypes = paramProjectType => {
  const projectType = parseMatchParamToProjectType(paramProjectType)
  return getTypeAndSubtypesArr(projectType)
}

export const areSubProjectsPossible = apiProjectType => {
  switch (apiProjectType) {
    case ApiProjectTypes.VOV:
    case ApiSeriesTypes.TV_EPISODE:
      return false

    default: return true
  }
}

export const isShotWizardPageOpened = pathname => {
  return pathname.startsWith('/editor/') && pathname.includes('/shots/')
}

export const isTimelineTagsPageOpened = pathname => {
  return pathname.startsWith('/editor/') && pathname.includes('/timeline-tags/')
}

// export const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1)

export const capitalize = (str: string, lower: boolean | number = false) => ((lower && typeof lower === 'boolean') ? str.toLowerCase() : str)
  .replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase())

export const parseCamelCase = value => value.split(/(?=[A-Z])/).map(capitalize).join(' ')
export const capitalizeText = value => value.split(' ').map(capitalize).join(' ')

export const isNull = value => value === null || value === undefined

export const isDescending =
  (direction: SortDirection) => direction === SortDirection.DESC

export const getDirectionSymbol = (direction: SortDirection) => {
  return isDescending(direction) ? '▼' : '▲'
}

export const parseToCatalog = <T = object> (
  arr: T[],
  catalog = {},
  key?: keyof T
): { [key: string]: T } => {
  const indexKey: any = key || 'id'
  arr.forEach(e => { catalog[e[indexKey]] = e })
  return catalog
}

export const formatDuration = (duration: number) => {
  function customTemplate () {
    const entityLabel = this.duration.asMinutes() < 1
      ? 'sec'
      : this.duration.asHours() < 1
        ? 'mins'
        : 'hrs'
    return `h:m:ss.SS [${entityLabel}]`
  }
  if (duration === 0) {
    return '0 sec'
  }
  return moment.duration(duration, 'milliseconds').format(customTemplate)
}

export const formatCirclesDuration = (duration: number) => {
  if (duration === 0) {
    return '0'
  }
  return moment.duration(duration, 'milliseconds').format('h:m:ss.SS')
}

export const formatDurationTable = (duration: number) => {
  if (duration === 0) {
    return '0'
  }
  return moment.duration(duration, 'milliseconds').format('h [h] m [min] s [sec] SS [ms]')
}

export const formatPrice = (price: number, quantity: number = 1, currency: string = 'USD'): string => {
  const newPrice = price * quantity

  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(newPrice)
}

export const noImageAvatar = 'https://www.securities-services.societegenerale.com/uploads/tx_bisgbio/default-profile.png'

export const getSortingResult = (a: any, b: any, direction: SortDirection) => {
  let result = 0
  if (a > b) { result = -1 }
  if (a < b) { result = 1 }

  return isDescending(direction) ? result : -result
}

export const getTableCellWidth = (
  widthPercentage: number,
  widthPx: number,
  widthString?: string
): CSSProperties => {
  switch (true) {
    case Boolean(widthPercentage): {
      return { flexBasis: `${widthPercentage}%`, maxWidth: `${widthPercentage}%` }
    }
    case Boolean(widthPx): {
      return { width: widthPx, minWidth: widthPx, maxWidth: widthPx }
    }
    case Boolean(widthString): {
      return { width: widthString, minWidth: widthString, maxWidth: widthString }
    }
    default: return {}
  }
}

export const initialFilterRange: FilterRange = {
  min: null,
  max: null
}

export const getTableHeight = (numberOfRows: number): number => {
  return Math.min(window.innerHeight * 0.5, numberOfRows * Number(styleAssets.rowHeightNum)) // change 0.7 to 0.5 for better responsiveness
}

export const isProjectPublished = (status: StatusTypes): boolean => {
  return status !== StatusTypes.NOT_PUBLISHED && status !== StatusTypes.NEW
}

export const parseToGalleryImage = (img: MediaContentDTO): GalleryImage => {
  return {
    ...img,
    src: img.url,
    status: FileStatus.UPLOADED
  }
}

export async function asyncForEach<T = any> (
  array: T[],
  callback: (element: T, index: number, array: T[]
  ) => Promise<void>) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array)
  }
}

export const parseQueryParams = (queryParams: object): string => {
  const formatValue = (value): string => {
    if (Array.isArray(value)) return value.map(v => encodeURIComponent(v)).join(',')
    return encodeURIComponent(value)
  }
  return Object.entries(queryParams || {})
    .map(([key, value]) => `${encodeURIComponent(key)}=${formatValue(value)}`)
    .join('&')
}

export const applyProjectQuery = (project: ProjectDTO, queryParams: any): ProjectDTO => {
  const children: ProjectDTO[] = project.children?.length
    ? project.children.map(p => applyProjectQuery(p, queryParams))
    : []
  return {
    ...project,
    children,
    loadContents: queryParams?.loadContents || false,
    loadHierarchy: queryParams?.loadHierarchy || false
  }
}

export const applyBrandQuery = (brand: BrandDTO, queryParams: any): BrandDTO => {
  return {
    ...brand,
    loadContents: queryParams?.loadContents || false,
    loadProjects: queryParams?.loadProjects || false
  }
}

export const applyProductQuery = (product: ProductDTO, queryParams: any): ProductDTO => {
  return {
    ...product,
    loadContents: queryParams?.loadContents || false,
    loadProjects: queryParams?.loadProjects || false
  }
}

export const getStartDateForPeriod = (filterPeriod: FilterPeriods): number => {
  switch (filterPeriod) {
    case FilterPeriods.TODAY:
      return moment().subtract(1, 'days').valueOf()
    case FilterPeriods.PAST_WEEK:
      return moment().subtract(7, 'days').valueOf()
    case FilterPeriods.PAST_MONTH:
      return moment().subtract(1, 'months').valueOf()
    case FilterPeriods.PAST_3_MONTHS:
      return moment().subtract(3, 'months').valueOf()
    default: return moment().valueOf()
  }
}

export const getStartAndEndDates = (
  period: OnSelectCallbackParam,
  filterPeriod: FilterPeriods
): { startDate: number, endDate: number } => {
  if (!period && filterPeriod !== FilterPeriods.CUSTOM) {
    return {
      startDate: getStartDateForPeriod(filterPeriod),
      endDate: moment().valueOf()
    }
  }

  return {
    startDate: period ? period.start.valueOf() : null,
    endDate: period ? period.end.valueOf() : null
  }
}

export const handleImageVideoUrl = (src): boolean => {
  if (!src) return true
  if (src.includes('video/mp4') || src.includes('.mp4')) {
    return false
  }
  return true
}

export const convertTimeStringToSeconds = (timeString:string):string => {
  const momentTime = moment(timeString, 'HH:mm:ss')

  return String(momentTime.hours() * 3600 + momentTime.minutes() * 60 + momentTime.seconds())
}

export const convertSecondsToTimeString = (seconds: number): string => {
  const timeString = '00:00:00'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  const timeStringSplit = timeString.split(':')

  if (hours) timeStringSplit[0] = hours < 10 ? `0${hours}` : `${hours}`
  if (minutes) timeStringSplit[1] = minutes < 10 ? `0${minutes}` : `${minutes}`
  if (secs) timeStringSplit[2] = secs < 10 ? `0${secs}` : `${secs}`

  return timeStringSplit.join(':')
}
